import { useCallback, useEffect, useState } from "react";
import { useLocalParticipant, useRoomContext } from "@livekit/components-react";
import { RoomEvent } from "livekit-client";
import type { ProviderRoleChange } from "../types";
import type { SendSignalMessage } from "./helpers";
import {
  ACTION_TYPES,
  type RoleChangeRequest,
  convertParticipantToPeer,
  sendSignal,
} from "./helpers";

const HOST_ROLE = "host";
const SUPPORTED_ROLE_CHANGE_ROLES = [HOST_ROLE];

const decoder = new TextDecoder();
const decodeRequest = (message: Uint8Array): RoleChangeRequest | null => {
  const strData = decoder.decode(message);
  const action = JSON.parse(strData) as SendSignalMessage;
  const { type, data } = action || {};
  if (!data || type !== ACTION_TYPES.REQUEST_ROLE_CHANGE) {
    return null;
  }
  return data;
};

// useLivekitRoleChange does nothing as we handle role change on server side
export const useLivekitRoleChange = (): ProviderRoleChange => {
  const room = useRoomContext();
  const [roleChangeRequest, setRoleChangeRequest] =
    useState<RoleChangeRequest | null>(null);
  const { localParticipant } = useLocalParticipant();
  const handleRoleChangeRequest = useCallback(
    (data: Uint8Array) => {
      try {
        const request = decodeRequest(data);
        if (
          !!request &&
          SUPPORTED_ROLE_CHANGE_ROLES.includes(request.role) &&
          request.participantId === localParticipant?.identity
        ) {
          setRoleChangeRequest(request);
        }
      } catch (error) {
        console.error("Error decoding role change request", error);
      }
    },
    [localParticipant?.identity],
  );

  useEffect(() => {
    if (room) {
      room.on(RoomEvent.DataReceived, handleRoleChangeRequest);
    }

    return () => {
      if (room) {
        room.off(RoomEvent.DataReceived, handleRoleChangeRequest);
      }
    };
  }, [room, handleRoleChangeRequest]);

  const requestRoleChange = async (
    participantId: string,
    role: string,
    force?: boolean,
  ) => {
    if (force || !SUPPORTED_ROLE_CHANGE_ROLES.includes(role)) {
      // Everything happens in the server side, so we don't need to send a request
      return Promise.resolve();
    }
    const data: RoleChangeRequest = {
      participantId,
      role,
      requestedBy: convertParticipantToPeer(localParticipant),
    };
    return sendSignal(
      ACTION_TYPES.REQUEST_ROLE_CHANGE,
      localParticipant,
      participantId,
      data,
    );
  };

  const acceptRoleChange = () => {
    setRoleChangeRequest(null);
    return Promise.resolve();
  };

  const rejectRoleChange = () => {
    setRoleChangeRequest(null);
  };

  const requestedByPeer = roleChangeRequest?.requestedBy || null;

  return {
    hasRoleChangeRequest: !!roleChangeRequest,
    requestedByPeer,
    requestRoleChange,
    acceptRoleChange,
    rejectRoleChange,
  };
};
